<template>
	<div data-test="session-console" class="flex-grow flex flex-col overflow-hidden">
		<SessionConsoleToolbar
			v-if="hasEventsToShow"
			:toggleGroups="toggleGroups"
			:displayedToggleGroups="displayedToggleGroups"
			:hiddenConsoleToggleGroups="hiddenConsoleToggleGroups"
			class="mt-0 mb-20"
			@toggle="toggleConsoleEventGroup"
		/>
		<div
			v-if="hasEventsToShow"
			ref="consoleContent"
			class="relative overflow-auto flex-grow visible-scrollbars pb-64"
			:class="{'pt-40': showTrimmingEventsIndicators}"
		>
			<table v-if="displayedEvents?.length" class="text-code text-gray-300">
				<tbody>
					<tr
						v-for="(event, index) in displayedEvents"
						:key="index"
						ref="row"
						class="console-item relative hover:bg-gray-650 cursor-pointer"
						:class="{
							'console-item-faded': isEventWithinTrimStartAndEndSeek(index),
							'bg-gray-650': selectedRowIndex === index,
						}"
						:style="[consoleRowStyle(index, event)]"
						@click="selectRow(index)"
					>
						<td ref="time" class="pl-24" :class="{'whitespace-nowrap': !isWrap}">
							<BaseTooltip placement="right">
								<button
									:disabled="!canSeek"
									class="console-item-time relative"
									@click="seek(displayedEventsSeekSeconds[index])"
								>
									{{ formatEventTime(event) }}
									<BaseIcon
										v-if="index === seekedEventIndex && canSeek && !domTrimming.isTrimming"
										ref="timeIndicator"
										name="mono/triangle-right"
										:style="{transform: `translateY(${seekOffsetPx}px)`}"
										class="current-time-indicator"
										tabindex="-1"
									/>
								</button>
								<template #text>
									<div>
										<span class="text-code-strong">Event recorded at:</span>
										{{ formatEventTimeDetails(event) }}
									</div>
								</template>
							</BaseTooltip>
						</td>
						<!-- Trim Start Indicator -->
						<td
							ref="startTrimIndicator"
							class="absolute"
							:style="[hasNoEventsBetweenTrimStartAndEnd ? {top: '-35px'} : {top: '-32px'}]"
						>
							<div
								v-if="index === trimStartEventIndex + 1 && canSeek && showTrimmingEventsIndicators"
								class="inline-flex"
							>
								<div
									class="text-center rounded-full bg-gray-700 border border-gray-900"
									:class="{'px-12': hasNoEventsBetweenTrimStartAndEnd}"
									style="min-width: 64px; z-index: 1"
								>
									{{ hasNoEventsBetweenTrimStartAndEnd ? 'Start / End' : 'Start' }}
								</div>
								<div class="border-b border-gray-900 absolute trim-indicator-bar" />
							</div>
						</td>

						<!-- Trim End Indicator -->
						<td ref="endTrimIndicator" class="absolute" style="bottom: 12px">
							<div
								v-if="index === trimEndEventIndex - 1 && canSeek && showTrimmingEventsIndicators"
								class="inline-flex"
							>
								<div class="border-b border-gray-900 absolute trim-indicator-bar" />
								<span
									class="text-center rounded-full bg-gray-700 border border-gray-900"
									:class="{'px-12': hasNoEventsBetweenTrimStartAndEnd}"
									style="min-width: 64px; z-index: 1"
								>
									{{ hasNoEventsBetweenTrimStartAndEnd ? 'Start / End' : 'End' }}
								</span>
							</div>
						</td>

						<!-- event count  -->
						<td>
							<div
								v-if="event.count > 1"
								:style="{backgroundColor: `var(--${getEventColor(event)})`}"
								class="event-count"
							>
								{{ event.count }}
							</div>
						</td>
						<td>
							<BaseIcon
								v-if="getEventIcon(event)"
								:name="getEventIcon(event)"
								:style="{color: `var(--${getEventColor(event)})`}"
								class="block mt-1 w-32 mr-0"
							/>
						</td>
						<td>
							<ConsoleEventError v-if="event.type.startsWith('error')" :event="event" :wrap="isWrap" />
							<ConsoleEventArgs
								v-else-if="event.type === 'assert'"
								:args="getAssertArgs(event)"
								class="text-error"
								:wrap="isWrap"
							/>
							<div
								v-else-if="consoleMethodsWithAtMostSingleStringArg.includes(event.type)"
								:class="consoleEntryWrappingClass"
							>
								<code
									class="text-gray-400"
									:class="[consoleEntryWrappingClass, isWrap ? 'break-all' : '']"
								>
									console.{{ event.type }}({{ event.args[0] ? '"' + event.args[0] + '"' : '' }})
								</code>
							</div>
							<ConsoleEventArgs
								v-else-if="event.source === 'console'"
								:args="event.args"
								:wrap="isWrap"
							/>
							<div v-else-if="event.type === 'start'" :class="consoleEntryWrappingClass">
								Session started
								<template v-if="event.url">
									on
									<ConsoleLink :href="event.url" target="_blank" rel="noreferrer">
										{{ event.url }}
									</ConsoleLink>
								</template>
							</div>
							<template v-else-if="event.type === 'end'">
								<template v-if="event.isInstantReplay"> Instant Replay clicked </template>
								<div v-else-if="event.url" :class="consoleEntryWrappingClass">
									Session ended on
									<ConsoleLink
										:href="event.url"
										target="_blank"
										rel="noreferrer"
										:class="consoleEntryWrappingClass"
									>
										{{ event.url }}
									</ConsoleLink>
								</div>
								<template v-else> Session ended </template>
							</template>
							<ConsoleStep v-else-if="event.type === 'step'" :event="event" :wrap="isWrap" />

							<!-- LOCALSTORAGE -->
							<SessionConsoleStorageEvent
								v-else-if="event.source === 'storage'"
								:event="event"
								:isWrap="isWrap"
								:class="consoleEntryWrappingClass"
							/>

							<div v-else-if="event.type === 'click'" :class="consoleEntryWrappingClass">
								Clicked
								<ConsoleEventArg renderAs="ArgHtml" :value="getEventTarget(event)" :wrap="isWrap" />
							</div>
							<ConsoleEventArg
								v-else-if="['keydown', 'input'].includes(event.type)"
								renderAs="ArgKeystrokes"
								:target="getEventTarget(event)"
								:type="event.type"
								:keys="event.keys"
								class="mr-8"
								:wrap="isWrap"
							/>
							<div v-else-if="event.type === 'videoStart'" :class="consoleEntryWrappingClass">
								----- Screen Recording Start -----
							</div>
							<div v-else-if="event.type === 'videoPause'" :class="consoleEntryWrappingClass">
								----- Screen Recording Paused on
								<ConsoleLink :href="event.url" target="_blank" rel="noreferrer">
									{{ event.url }}
								</ConsoleLink>
								-----
							</div>
							<div v-else-if="event.type === 'videoResume'" :class="consoleEntryWrappingClass">
								----- Screen Recording Resumed on
								<ConsoleLink :href="event.url" target="_blank" rel="noreferrer">
									{{ event.url }}
								</ConsoleLink>
								-----
							</div>
							<div v-else-if="event.type === 'videoStop'" :class="consoleEntryWrappingClass">
								----- Screen Recording End -----
							</div>
							<div v-else-if="event.type === 'screenshot'">Screenshot taken</div>
							<div
								v-else-if="event.type.startsWith('network')"
								class="text-error"
								:class="[consoleEntryWrappingClass, {'break-all': isWrap}]"
							>
								{{ event.method }}
								<component
									:is="spanOrButton"
									class="underline text-left text-gray-400 hover:text-gray-300"
									@click.stop="openNetworkRequest(event)"
								>
									{{ event.url }}
								</component>
								{{ event.error || event.statusCode }}
							</div>
							<template v-else> [[UNRECOGNIZED EVENT TYPE]] </template>
						</td>
					</tr>
				</tbody>
			</table>

			<div v-else class="font-mono text-sm p-24 text-gray-300 ml-2">
				Current filters return 0 events.
				<button class="link-blue" @click="toggleConsoleEventGroup('all')">See all events.</button>
			</div>

			<SessionDevToolsQuickReply class="mt-48" />
		</div>
		<div
			v-else-if="sessionEventsStore.rawEvents && !sessionEventsStore.processedEvents"
			class="text-code text-gray-300 pt-16 pb-24 px-24"
		>
			Loading
			<BaseIcon name="mono/load" class="w-12 h-12 pt-2 ml-2 align-baseline" />
		</div>
		<div v-else class="text-code text-gray-300 overflow-y-auto pt-16 pb-24 px-24">
			<p class="mb-8">This session does not contain any console logs.</p>
			<p v-if="!$store.isExtension">
				Check out this
				<a :href="appUrl + '/sessions/walkthrough'" target="_blank" class="link-blue"> sample session </a>
				to see the type of data {{ $store.productName }} records.
			</p>
			<p v-else-if="sessionStore.session.isInstantReplay">
				Replays are enabled though, so feel free to close this tab <br />
				and try again after there has been some action on your page.
			</p>
			<p v-else>
				To include technical logs with screenshots, please enable
				<a href="https://birdeatsbug.com/replays" target="_blank" class="link-blue">Replays</a>
				in the extension. Or check out this
				<a :href="appUrl + '/sessions/walkthrough'" target="_blank" class="link-blue"> sample session </a>
				to see the type of data {{ $store.productName }} records.
			</p>
		</div>
	</div>
</template>

<script>
import throttle from 'lodash/throttle'
import {ref} from 'vue'
import {useStorage} from '@vueuse/core'
import {storeToRefs} from 'pinia'

import ConsoleEventError from 'shared/components/ConsoleEventError.vue'
import ConsoleEventArgs from 'shared/components/ConsoleEventArgs.vue'
import ConsoleLink from 'shared/components/ConsoleLink.vue'
import ConsoleStep from 'shared/components/ConsoleStep.vue'
import SessionDevToolsQuickReply from 'shared/components/SessionDevToolsQuickReply.vue'
import SessionConsoleToolbar from 'shared/components/SessionConsoleToolbar.vue'
import analytics from 'shared/utils/analytics'
import {humanDurationShort} from 'shared/utils/humanDuration'
import humanTimestamp from 'shared/utils/humanTimestamp'
import sanitizeUrl from 'shared/utils/sanitizeUrl'
import {useSessionStore} from 'shared/stores/session'
import {useSessionEventsStore} from 'shared/stores/sessionEvents'
import {useRouteQuery} from 'shared/composables/useRouteQuery'
import SessionConsoleStorageEvent from './SessionConsoleStorageEvent.vue'

export default {
	name: 'SessionConsole',
	components: {
		ConsoleEventError,
		ConsoleEventArgs,
		ConsoleLink,
		ConsoleStep,
		SessionDevToolsQuickReply,
		SessionConsoleToolbar,
		SessionConsoleStorageEvent,
	},
	setup() {
		const hiddenConsoleToggleGroupsStorage = useStorage('hiddenConsoleToggleGroups')
		const hiddenConsoleToggleGroupsQuery = useRouteQuery('consolePaneHiddenGroups', undefined)
		const hiddenConsoleToggleGroups = ref(
			hiddenConsoleToggleGroupsQuery.value?.split('&') || hiddenConsoleToggleGroupsStorage.value?.split(',') || []
		)

		const sessionStore = useSessionStore()
		const sessionEventsStore = useSessionEventsStore()
		const {canSeek, domTrimming, currentTimeSeconds, seekSeconds} = storeToRefs(sessionStore)
		const {processedEvents} = storeToRefs(sessionEventsStore)

		return {
			canSeek,
			domTrimming,
			currentTimeSeconds,
			seekSeconds,
			seek: sessionStore.seek,
			sessionStore,
			processedEvents,
			sessionEventsStore,
			hiddenConsoleToggleGroupsQuery,
			hiddenConsoleToggleGroupsStorage,
			hiddenConsoleToggleGroups,
		}
	},
	data() {
		return {
			appUrl: import.meta.env.VUE_APP_URL,
			trimmedRowDefaultHeight: 66,
			currentTime: undefined,
			currentTimeIndicatorTop: 0,
			currentTimeIndicatorTansitionDuration: '300ms',
			allRowsHeight: [],
			selectedRowIndex: parseInt(this.$route.query.consoleIndex),
			toggleGroups: {
				Step: {icon: 'mono/step', class: 'text-blue-500', color: 'blue-500'},
				Click: {icon: 'mono/click', color: 'gray-400'},
				Keystroke: {icon: 'mono/keystroke', color: 'gray-400'},
				Log: {icon: 'mono/log', color: 'gray-400'},
				Warning: {icon: 'mono/warn', class: 'text-warn', color: 'yellow-500'},
				Error: {icon: 'mono/error', class: 'text-error', color: 'red-500'},
				Assertion: {icon: 'mono/assert', class: 'text-error', color: 'red-500'},
				'Network Error': {icon: 'mono/network-error', class: 'text-error', color: 'red-500'},
				'Blocked Request': {icon: 'mono/block', class: 'text-error', color: 'red-500'},
				Storage: {icon: 'mono/storage', color: 'gray-400'},
				videoStart: {
					icon: 'mono/video-start',
					class: 'text-green-500',
					color: 'green-500',
					hideToggleGroup: true,
				},
				videoPause: {
					icon: 'mono/video-pause',
					class: 'text-green-500',
					color: 'green-500',
					hideToggleGroup: true,
				},
				videoResume: {
					icon: 'mono/video-start',
					class: 'text-green-500',
					color: 'green-500',
					hideToggleGroup: true,
				},
				videoStop: {
					icon: 'mono/video-stop',
					class: 'text-green-500',
					color: 'green-500',
					hideToggleGroup: true,
				},
				screenshot: {
					icon: 'mono/screenshot',
					class: 'text-green-500',
					color: 'green-500',
					hideToggleGroup: true,
				},
				'Instant Replay': {
					icon: 'mono/console',
					class: 'text-green-500',
					color: 'green-500',
					hideToggleGroup: true,
				},
				Dir: {icon: 'mono/dir', color: 'gray-400'},
				Group: {icon: 'mono/group', color: 'gray-400'},
				Table: {icon: 'mono/union', color: 'gray-400'},
				Count: {icon: 'mono/count', color: 'gray-400'},
				Profile: {icon: 'mono/performance', color: 'gray-400'},
				Time: {icon: 'mono/time', color: 'gray-400'},
				Trace: {icon: 'mono/step', color: 'gray-400'},
			},
			eventTypeToggleGroupMapping: {
				start: 'Step',
				end: 'Step',
				step: 'Step',
				click: 'Click',
				keydown: 'Keystroke',
				input: 'Keystroke',
				log: 'Log',
				info: 'Log',
				debug: 'Log',
				warn: 'Warning',
				error: 'Error',
				'error-uncaught': 'Error',
				'error-promise': 'Error',
				assert: 'Assertion',
				network: 'Network Error',
				networkBlocked: 'Blocked Request',
				videoStart: 'videoStart',
				videoPause: 'videoPause',
				videoResume: 'videoResume',
				videoStop: 'videoStop',
				screenshot: 'screenshot',
				instantReplay: 'Instant Replay',
				dir: 'Dir',
				dirxml: 'Dir',
				group: 'Group',
				groupCollapsed: 'Group',
				groupEnd: 'Group',
				table: 'Table',
				count: 'Count',
				countReset: 'Count',
				profile: 'Profile',
				profileEnd: 'Profile',
				time: 'Time',
				timeEnd: 'Time',
				timeLog: 'Time',
				timeStamp: 'Time',
				trace: 'Trace',
				localStorage: 'Storage',
				sessionStorage: 'Storage',
			},
			consoleMethodsWithAtMostSingleStringArg: [
				'count',
				'countReset',
				'group',
				'groupCollapsed',
				'groupEnd',
				'profile',
				'profileEnd',
				'time',
				'timeEnd',
				'timeLog',
				'timeStamp',
			],
		}
	},
	computed: {
		hasEventsToShow() {
			if (!this.$store.isExtension) return this.processedEvents.length > 0
			// For screenshot sessions without events besides session start, end, screenshot,
			// we prefer to show the empty state encouraging to enable Instant Replay as onboarding.
			return Boolean(
				this.uniqueEventTypes.find(type => type !== 'start' && type !== 'end' && type !== 'screenshot')
			)
		},
		consoleEntryWrappingClass() {
			return this.isWrap ? 'whitespace-normal' : 'whitespace-nowrap'
		},
		isWrap() {
			return this.$sessionLayout.wrapDevToolsText
		},
		spanOrButton() {
			return this.isWrap ? 'span' : 'button'
		},
		uniqueEventTypes() {
			return [...new Set(this.processedEvents.map(request => request.type))]
		},
		displayedToggleGroups() {
			const eventTypesToggleGroupMappings = this.uniqueEventTypes.map(
				eventType => this.eventTypeToggleGroupMapping[eventType]
			)
			return Object.entries(this.toggleGroups)
				.filter(
					([toggleGroupName, toggleGroup]) =>
						eventTypesToggleGroupMappings.includes(toggleGroupName) && !toggleGroup.hideToggleGroup
				)
				.map(([toggleGroupName]) => toggleGroupName)
		},
		displayedEvents() {
			return this.processedEvents.filter(
				event => !this.hiddenConsoleToggleGroups.includes(this.eventTypeToggleGroupMapping[event.type])
			)
		},
		displayedEventsSeekSeconds() {
			return this.displayedEvents?.map(this.calculateSeekSeconds)
		},
		trimHandleRef() {
			if (this.domTrimming.activeTrimHandle === 'start') {
				const indicatorIndex = this.trimStartEventIndex < 0 ? 0 : this.trimStartEventIndex
				return this.$refs.startTrimIndicator?.[indicatorIndex]
			} else if (this.domTrimming.activeTrimHandle === 'end') {
				const indicatorIndex =
					this.trimEndEventIndex >= this.displayedEvents.length
						? this.displayedEvents.length - 1
						: this.trimEndEventIndex

				return this.$refs.endTrimIndicator?.[indicatorIndex]
			}
			return null
		},
		replayTotalTimeInSeconds() {
			return this.domTrimming.replayTotalTimeInMs / 1000
		},
		showTrimmingEventsIndicators() {
			return (
				this.domTrimming.isTrimming ||
				this.domTrimming.trimStartAtSecond ||
				(this.domTrimming.trimEndAtSecond &&
					Math.floor(this.domTrimming.trimEndAtSecond) < Math.floor(this.replayTotalTimeInSeconds))
			)
		},
		trimStartEventIndex() {
			if (!this.domTrimming.trimStartAtSecond) return -1

			const index = this.displayedEventsSeekSeconds.findLastIndex?.(seconds => {
				return seconds <= this.domTrimming.trimStartAtSecond
			})
			return index
		},
		trimEndEventIndex() {
			// if no trimAtEnd exits, or the trimEndAtSecond is equal to the last displayEventSeekSeconds
			// we show the trim end indicator after the last console item
			if (
				!this.domTrimming.trimEndAtSecond ||
				Math.floor(this.domTrimming.trimEndAtSecond) >=
					Math.floor(Math.max.apply(Math, this.displayedEventsSeekSeconds))
			) {
				return this.displayedEvents.length
			}

			const index = this.displayedEventsSeekSeconds?.findIndex(
				seconds => seconds >= this.domTrimming.trimEndAtSecond
			)

			if (index === -1) return this.displayedEvents.length
			return index
		},
		hasNoEventsBetweenTrimStartAndEnd() {
			const firstDisplayedEventSeekSeconds = this.displayedEventsSeekSeconds[0]
			const lastDisplayedEventSeekSeconds =
				this.displayedEventsSeekSeconds[this.displayedEventsSeekSeconds.length - 1]
			return (
				this.trimStartEventIndex >= this.trimEndEventIndex ||
				this.trimEndEventIndex <= this.trimStartEventIndex ||
				this.domTrimming.trimStartAtSecond > lastDisplayedEventSeekSeconds ||
				this.domTrimming.trimEndAtSecond < firstDisplayedEventSeekSeconds ||
				// here we check if domTrimming.trimStartAtSecond and domTrimming.trimEndAtSecond current values has no displayed events seconds
				// in b/w them. For example: if displayedEventsSeekSeconds = [2,8,10,12], and domTrimming.trimStartAtSecond=3 and domTrimming.trimEndAtSecond=7
				// then there are no events between trim start and trim end
				!this.displayedEventsSeekSeconds.some(seconds => {
					if (!this.domTrimming.trimStartAtSecond) return true
					if (!this.domTrimming.trimEndAtSecond) return true
					if (Math.floor(this.domTrimming.trimEndAtSecond) === Math.floor(lastDisplayedEventSeekSeconds))
						return true
					return seconds >= this.domTrimming.trimStartAtSecond && seconds <= this.domTrimming.trimEndAtSecond
				})
			)
		},
		seekedEventIndex() {
			if (this.currentTimeSeconds === undefined) return
			const index = this.displayedEventsSeekSeconds.findLastIndex?.(seconds => seconds <= this.currentTimeSeconds)
			if (index === -1) return
			return index
		},
		seekIntervalSec() {
			if (this.seekedEventIndex === undefined) return
			return (
				this.displayedEventsSeekSeconds[this.seekedEventIndex + 1] -
				this.displayedEventsSeekSeconds[this.seekedEventIndex]
			)
		},
		seekIntervalPx() {
			if (this.seekedEventIndex === undefined) return 0
			const seekedEventRef = this.$refs.time && this.$refs.time[this.seekedEventIndex]
			if (seekedEventRef?.offsetParent?.offsetTop === undefined) return 0
			const afterSeekedEventRef = this.$refs.time && this.$refs.time[this.seekedEventIndex + 1]
			if (afterSeekedEventRef?.offsetParent?.offsetTop === undefined) return 0
			return afterSeekedEventRef.offsetParent.offsetTop - seekedEventRef.offsetParent.offsetTop
		},
		seekOffsetSec() {
			if (this.seekedEventIndex === undefined) return
			return this.currentTimeSeconds - this.displayedEventsSeekSeconds[this.seekedEventIndex]
		},
		seekOffsetPx() {
			if (this.seekedEventIndex === undefined) return 0
			return (this.seekIntervalPx / this.seekIntervalSec) * this.seekOffsetSec
		},

		rowHeightTrimStartEventIndex() {
			if (!this.allRowsHeight.length) return
			const height = this.allRowsHeight?.[this.trimStartEventIndex]
			return height > 30 ? height + this.trimmedRowDefaultHeight : this.trimmedRowDefaultHeight
		},
		rowHeightTrimEndEventIndex() {
			if (!this.allRowsHeight.length) return
			const height = this.allRowsHeight[this.trimEndEventIndex - 1]
			return height > 30 ? height + this.trimmedRowDefaultHeight : this.trimmedRowDefaultHeight
		},
	},
	watch: {
		processedEvents: {
			immediate: true,
			handler: function (events, previousEvents) {
				if (previousEvents?.length) return
				if (this.$route.query.consolePaneHiddenGroups) return
				// When there's too many events to render performantly, only show "Steps" event group
				// - unless the user explicitly enabled toggle groups on this session as indicated by query params.
				if (events?.length > 200)
					this.hiddenConsoleToggleGroups = Object.keys(this.toggleGroups).filter(
						toggleGroup => toggleGroup !== 'Step'
					)
			},
		},
		// we watch for `seekOffsetPx` (instead of `currentTimeSeconds`) to account for when the user clicks on a timestamp
		// directly: in that case, we do not want to `.scrollIntoView()` because the clicked timestamp is already
		// in view (`seekOffsetPx` will be 0, while `currentTimeSeconds` will always have a value)
		seekOffsetPx: throttle(async function (newVal) {
			if (!newVal || !this.$sessionLayout.scrollDevToolsWithPlayback) return
			await this.$nextTick()
			this.$refs.timeIndicator?.[0]?.$el?.scrollIntoView({behavior: 'smooth', block: 'center'})
		}, 1000),
		// initially when no trimStartEventIndex/trimEndEventIndex exists, we want to scroll to
		// either the start most or end most trim indicator. After trimming starts the watchers
		// (trimStartEventIndex, trimEndEventIndex) are fired and the respective refs are used
		// to scroll into the view
		showTrimmingEventsIndicators: {
			immediate: true,
			handler: function () {
				this.trimHandleRef?.scrollIntoView({behavior: 'smooth', block: 'end'})
			},
		},
		trimStartEventIndex: {
			handler: function () {
				const indicatorIndex = this.trimStartEventIndex < 0 ? 0 : this.trimStartEventIndex
				this.$refs.startTrimIndicator?.[indicatorIndex]?.scrollIntoView({
					behavior: 'smooth',
					block: 'end',
				})
			},
		},
		trimEndEventIndex: {
			handler: function () {
				const indicatorIndex = this.trimEndEventIndex < 0 ? 0 : this.trimEndEventIndex
				this.$refs.endTrimIndicator?.[indicatorIndex]?.scrollIntoView({
					behavior: 'smooth',
					block: 'end',
				})
			},
		},
		displayedEvents: {
			handler: function (displayedEvents, previousDisplayedEvents) {
				if (previousDisplayedEvents?.length) return
				if (!displayedEvents?.length) return
				if (!this.hasEventsToShow) return
				this.scrollOnInitialRender()
				this.computeAllRowsHeight()
			},
			immediate: true,
		},
	},
	methods: {
		isEventWithinTrimStartAndEndSeek(index) {
			return (
				(this.domTrimming.trimStartAtSecond && index <= this.trimStartEventIndex) ||
				(this.domTrimming.trimEndAtSecond && index > this.trimEndEventIndex - 1)
			)
		},
		toggleConsoleEventGroup(groupName) {
			if (groupName === 'all') {
				this.hiddenConsoleToggleGroups =
					this.hiddenConsoleToggleGroups.length > 0 ? [] : this.displayedToggleGroups
			} else if (this.hiddenConsoleToggleGroups.includes(groupName)) {
				this.hiddenConsoleToggleGroups = this.hiddenConsoleToggleGroups.filter(
					entry => entry && entry !== groupName
				)
			} else {
				this.hiddenConsoleToggleGroups.push(groupName)
			}

			this.hiddenConsoleToggleGroupsStorage = this.hiddenConsoleToggleGroups
			this.hiddenConsoleToggleGroupsQuery = this.hiddenConsoleToggleGroups.length
				? this.hiddenConsoleToggleGroups.join('&')
				: undefined

			this.selectedRowIndex = undefined

			analytics.track('Console Event Group Toggled', {
				toggledGroup: groupName,
				hiddenGroups: this.hiddenConsoleToggleGroups,
			})
		},
		getEventIcon(event) {
			const eventType = event.isInstantReplay ? 'instantReplay' : event.type
			return this.toggleGroups[this.eventTypeToggleGroupMapping?.[eventType]]?.icon
		},
		getEventColor(event) {
			const eventType = event.isInstantReplay ? 'instantReplay' : event.type
			return this.toggleGroups[this.eventTypeToggleGroupMapping?.[eventType]]?.color
		},
		getEventTarget(event) {
			return event?.target?.serialized?.[0] || event?.target?.[0]?.serialized?.[0] // up until 8ce7433854849147d175807a9ace62dc84164b04, the click event target was serialized as array
		},
		getAssertArgs(event) {
			const [falsyAssertResult, ...args] = event.args // eslint-disable-line no-unused-vars
			return ['Assertion failed: ', ...args]
		},
		async openNetworkRequest(event) {
			if (event.requestId) {
				analytics.track(`Network Request Details Opened`, {from: 'Console'})
				await this.$routerHelpers.pushQueryParams({requestId: event.requestId})
				this.$sessionLayout.devToolsPane = 'network'
				return
			}

			// if there's no network request details (for old sessions), open the link directly
			const tab = window.open(sanitizeUrl(event.url), '_blank')
			tab?.focus()
		},
		async computeAllRowsHeight() {
			if (this.allRowsHeight.length) return
			await this.$nextTick()
			this.allRowsHeight = this.displayedEvents?.map(
				(_, index) => this.$refs.row?.[index]?.getBoundingClientRect()?.height
			)
		},
		async scrollOnInitialRender() {
			await this.$nextTick()
			// After initial render, scroll to bottom of events
			if (this.$route.query.consoleIndex) {
				this.$refs.row?.[this.$route.query.consoleIndex]?.scrollIntoView({block: 'center'})
				return
			}
			if (!this.currentTimeSeconds) {
				if (!this.$refs.consoleContent) return
				this.$refs.consoleContent.scrollTop = this.$refs.consoleContent.scrollHeight
			} else {
				// If there's already currentTimeSeconds progress, scroll events of seeked time into view
				this.$refs.timeIndicator?.[0]?.$el?.scrollIntoView({block: 'center'})
			}
		},
		async selectRow(index) {
			if (index === this.selectedRowIndex) {
				await this.$routerHelpers.replaceQueryParams({consoleIndex: null, seekSeconds: null})
				this.seekSeconds = undefined
				this.selectedRowIndex = undefined
				return
			}
			this.selectedRowIndex = index
			const seekSeconds = this.displayedEventsSeekSeconds[index]
			await this.$routerHelpers.replaceQueryParams({
				consoleIndex: index,
				seekSeconds: seekSeconds,
			})
			this.seek(seekSeconds)
		},
		consoleRowStyle(index, event) {
			let style = {
				'border-left': `3px solid transparent`,
			}
			if (index === this.selectedRowIndex) style = {'border-left': `3px solid ${this.getEventColor(event)}`}
			if (index === this.trimStartEventIndex) style.height = `${this.rowHeightTrimStartEventIndex}px`
			else if (
				index === this.trimEndEventIndex - 1 &&
				this.trimEndEventIndex - 1 !== this.displayedEvents.length - 1
			)
				style.height = `${this.rowHeightTrimEndEventIndex}px`
			return style
		},
		formatEventTime(event) {
			return humanDurationShort(event.millisecondsSinceSessionStart)
		},
		formatEventTimeDetails(event) {
			return humanTimestamp(event.millisecondsSinceSessionStart, event.createdAt)
		},
		calculateSeekSeconds(event) {
			return (event.millisecondsSinceSessionStart - this.sessionStore.videoOffsetInMilliseconds) / 1000
		},
	},
}
</script>

<style scoped>
.console-item {
	vertical-align: top;
	line-height: 16px;
	border-bottom: 1px solid transparent;
}

.console-item > td {
	padding-top: 2px;
	padding-bottom: 2px;
}

.console-item-time {
	color: var(--gray-400);
	min-width: 26px;
	margin-right: 8px;
}

.console-item-time:hover {
	color: var(--gray-300);
}

.console-item-time:disabled {
	color: var(--gray-500);
}

.current-time-indicator {
	position: absolute;
	left: -18px;
	top: 0px;
	font-size: 14px;
	will-change: transform;
	transition-property: transform;
	transition-timing-function: linear;
	transition-duration: 150ms;
	z-index: 10;
}

.event-count {
	min-width: 32px;
	font-size: 10px;
	text-align: center;
	color: #fff;
	border-radius: 10px;
	padding: 0px 2px 0px 2px;
	font-weight: 600;
}

.console-item-faded :deep(*) {
	color: var(--gray-400);
}

.console-item-faded :deep(.console-item-time) {
	color: var(--gray-500) !important;
}

.console-item-faded :deep(svg) {
	fill: var(--gray-400);
}

.console-item-faded :deep(ul > li) {
	color: var(--gray-400);
}

.console-item-faded :deep(ul > li.tag) {
	color: var(--gray-400);
}

.console-item-faded :deep(ul > li > .attribute) {
	color: var(--gray-400);
}

.console-item-faded :deep(ul > li > .control-character) {
	color: var(--gray-400);
}

.console-item-faded :deep(ul > li > .attribute-value) {
	color: var(--gray-400);
}

.console-item-faded :deep(.event-count) {
	color: var(--gray-500);
	background-color: var(--gray-600) !important;
}

.console-item-faded :deep(.text-error) {
	color: var(--gray-400) !important;
}

.trim-indicator-bar {
	width: 100vw;
	left: -65px;
	top: 12px;
	z-index: 0;
}
</style>
